import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Table,
  Modal,
  Form,
  Alert,
  Spinner,
  InputGroup
} from 'react-bootstrap';
import { useAuth } from '../utils/AuthContext';
import {
  fetchUserDocuments,
  uploadDocument,
  setActiveDocumentVersion,
  downloadDocument,
  searchDocuments,
  fetchCompleteProfile,
  deleteDocument
} from '../../utils/api';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { FaUpload, FaDownload, FaEye, FaSearch, FaCheck, FaFile, FaFilePdf, FaImage, FaTimes, FaExternalLinkAlt, FaTrash } from 'react-icons/fa';
import config from '../../config';
import debounce from 'lodash/debounce';
import '../styles/document-viewer.css';

const Documents = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentVersions, setDocumentVersions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupBy, setGroupBy] = useState('type');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [uploadData, setUploadData] = useState({
    document_type: '',
    file: null,
    notes: ''
  });
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [companyStatus, setCompanyStatus] = useState('pending');
  const [deleting, setDeleting] = useState(false);

  // Use profile completion hook
  const { percentage: profileCompletion, isComplete: profileComplete } = useProfileCompletion();

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query, companyId) => {
      if (!query.trim()) {
        setFilteredDocuments(documents);
        setSearching(false);
        return;
      }

      try {
        setSearching(true);
        console.log('🔎 Searching documents with query:', query);

        // First try local filtering
        const localFiltered = documents.map(docGroup => ({
          ...docGroup,
          documents: docGroup.documents.filter(doc =>
            doc.document_name.toLowerCase().includes(query.toLowerCase()) ||
            doc.original_filename.toLowerCase().includes(query.toLowerCase()) ||
            doc.description?.toLowerCase().includes(query.toLowerCase()) ||
            doc.company_info.company_name.toLowerCase().includes(query.toLowerCase()) ||
            docGroup.document_name.toLowerCase().includes(query.toLowerCase())
          )
        })).filter(group => group.documents.length > 0);

        setFilteredDocuments(localFiltered);

        // Then try API search if local search returns no results
        if (localFiltered.length === 0) {
          const response = await searchDocuments(companyId, query);
          console.log('🔍 API Search response:', response);

          if (response.success || response.status === 'success') {
            setFilteredDocuments(response.data || []);
          }
        }
      } catch (error) {
        console.error('❌ Error searching documents:', error);
        toast.error('❌ Search failed');
      } finally {
        setSearching(false);
      }
    }, 300),
    [documents]
  );

  // Clear search results
  const clearSearch = () => {
    setSearchQuery('');
    setFilteredDocuments(documents);
  };

  // Handle search input change
  const handleSearchChange = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    try {
      const companyId = await getCompanyId();
      debouncedSearch(query, companyId);
    } catch (error) {
      console.error('❌ Error getting company ID for search:', error);
    }
  };

  // Update filtered documents when main documents change
  useEffect(() => {
    setFilteredDocuments(documents);
  }, [documents]);

  const documentTypes = [
    { value: 'certificate_of_registration', label: 'Certificate of Registration' },
    { value: 'cr12_certificate', label: 'CR12 Certificate' },
    { value: 'kra_pin_certificate', label: 'KRA PIN Certificate' },
    { value: 'business_permit', label: 'Business Permit' },
    { value: 'tax_compliance_certificate', label: 'Tax Compliance Certificate' },
    { value: 'nssf_compliance_certificate', label: 'NSSF Compliance Certificate' },
    { value: 'sha_compliance_certificate', label: 'SHA Compliance Certificate' },
    { value: 'passport_photos', label: 'Passport Photos' },
    { value: 'national_id_passport', label: 'National ID/Passport' },
    { value: 'other', label: 'Other' }
  ];

  const getDocumentTypeLabel = (type) => {
    const docType = documentTypes.find(dt => dt.value === type);
    return docType ? docType.label : type;
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf':
        return <FaFilePdf className="text-danger" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <FaImage className="text-info" />;
      default:
        return <FaFile className="text-secondary" />;
    }
  };

  const getCompanyId = async () => {
    try {
      console.log('🔍 Fetching company ID for user:', user.id);
      const profileResponse = await fetchCompleteProfile(user.id);

      console.log('📋 Complete profile response:', profileResponse);

      if (!profileResponse.status === 'success' || !profileResponse.data?.company?.id) {
        throw new Error('Could not get company profile');
      }

      const companyId = profileResponse.data.company.id;
      const status = profileResponse.data.company.status || profileResponse.data.company.verification_status || 'pending';

      console.log('🏢 Found company ID:', companyId);
      console.log('📊 Company status:', status);

      // Set company status for UI decisions
      setCompanyStatus(status);

      // Log the company profile data for debugging
      console.log('📋 Company profile data:', {
        companyName: profileResponse.data.company.companyName,
        companyId: companyId,
        registrationNumber: profileResponse.data.company.registrationNumber,
        status: status
      });

      return companyId;
    } catch (error) {
      console.error('❌ Error getting company ID:', error);
      throw error;
    }
  };

  const fetchDocuments = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const companyId = await getCompanyId();

      console.log('📄 Fetching documents with params:', {
        companyId,
        groupBy,
        statusFilter,
        apiUrl: `${config.API_URL}documents?company_id=${companyId}&group_by=${groupBy}&status=${statusFilter}`
      });

      const response = await fetchUserDocuments(companyId, groupBy, statusFilter);

      console.log('📥 Documents response:', response);

      if (response.success || response.status === 'success') {
        const docs = response.data || [];
        console.log(`✅ Successfully fetched ${docs.length} documents`);
        console.log('📋 Documents:', docs);
        setDocuments(docs);
      } else {
        console.error('❌ Error fetching documents:', response.message);
        toast.error(`❌ Error: ${response.message}`);
      }
    } catch (error) {
      console.error('❌ Error fetching documents:', error);
      toast.error('❌ Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!user?.id) return;

    if (!uploadData.file || !uploadData.document_type) {
      toast.error('❌ Please select a file and document type');
      return;
    }

    try {
      setUploading(true);
      const companyId = await getCompanyId();

      console.log('📤 Uploading document with params:', {
        companyId,
        fileName: uploadData.file.name,
        documentType: uploadData.document_type,
        apiUrl: `${config.API_URL}documents/upload`
      });

      const formData = new FormData();
      formData.append('document', uploadData.file);
      formData.append('document_type', uploadData.document_type);
      formData.append('document_name', uploadData.file.name);
      formData.append('description', uploadData.notes);
      formData.append('upload_reason', 'User upload');
      formData.append('company_id', companyId);
      formData.append('user_id', user.id);

      // Log FormData contents
      console.log('📋 FormData contents:');
      for (let [key, value] of formData.entries()) {
        if (key === 'document') {
          console.log('document:', {
            name: value.name,
            type: value.type,
            size: value.size
          });
        } else {
          console.log(`${key}:`, value);
        }
      }

      const response = await uploadDocument(companyId, formData);

      console.log('📤 Upload response:', response);

      if (response.success) {
        console.log('✅ Document uploaded successfully');
        toast.success('✅ Document uploaded successfully!');
        setShowUploadModal(false);
        setUploadData({ document_type: '', file: null, notes: '' });
        fetchDocuments();
      } else {
        console.error('❌ Upload failed:', response);
        const errorMessage = response.message || 'Unknown error occurred';
        toast.error(`❌ Upload failed: ${errorMessage}`);
      }
    } catch (error) {
      console.error('❌ Error uploading document:', error);
      const errorMessage = error.message || 'Network error occurred';
      toast.error(`❌ Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSetActive = async (documentId, documentType) => {
    if (!user?.id) return;

    try {
      const result = await Swal.fire({
        title: 'Set Active Version',
        text: `Are you sure you want to set this as the active version for ${getDocumentTypeLabel(documentType)}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, set as active'
      });

      if (result.isConfirmed) {
        const companyId = await getCompanyId();

        const response = await setActiveDocumentVersion(companyId, documentId);

        if (response.success) {
          toast.success('✅ Document set as active successfully!');
          fetchDocuments();
          if (showVersionModal) {
            fetchDocumentVersions(selectedDocument.document_type);
          }
        } else {
          toast.error(`❌ Error: ${response.message}`);
        }
      }
    } catch (error) {
      console.error('Error setting active document:', error);
      toast.error('❌ Failed to set active document');
    }
  };

  const handleDeleteDocument = async (documentId, documentName) => {
    if (!user?.id) return;

    try {
      const result = await Swal.fire({
        title: 'Delete Document',
        text: `Are you sure you want to delete "${documentName}"? This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        setDeleting(true);

        const response = await deleteDocument(documentId);

        if (response.success || response.status === 'success') {
          toast.success('✅ Document deleted successfully!');
          fetchDocuments();
        } else {
          toast.error(`❌ Error: ${response.message || 'Failed to delete document'}`);
        }
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('❌ Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewDocument = (doc) => {
    const isPdf = doc.document_name.toLowerCase().endsWith('.pdf');
    const fileUrl = config.API_URL.replace('/api/', '/') + doc.file_path;

    console.log('🔍 Viewing document:', {
      filename: doc.original_filename,
      filePath: doc.file_path,
      fileUrl: fileUrl
    });

    // Common configuration for the popup
    const commonConfig = {
      width: '90%',
      height: 'auto',
      showCloseButton: true,
      showConfirmButton: false,
      background: '#000',
      padding: 0,
      customClass: {
        container: 'document-viewer-container',
        popup: 'document-viewer-popup',
        closeButton: 'document-viewer-close'
      }
    };

    if (isPdf) {
      Swal.fire({
        ...commonConfig,
        html: `<iframe src="${fileUrl}" width="100%" height="100%" frameborder="0"></iframe>`,
        didOpen: () => {
          // Add specific styling for PDF viewer
          const popup = Swal.getPopup();
          popup.style.overflow = 'hidden';
          const iframe = popup.querySelector('iframe');
          iframe.style.backgroundColor = 'white';
        }
      });
    } else {
      // For images
      Swal.fire({
        ...commonConfig,
        imageUrl: fileUrl,
        imageAlt: doc.original_filename,
        imageWidth: '100%',
        imageHeight: '100%',
        customClass: {
          ...commonConfig.customClass,
          image: 'document-viewer-image'
        }
      });
    }
  };

  const handleDownload = async (documentId, filename, filePath) => {
    if (!user?.id) return;

    console.log('🔍 Downloading document with params:', {
      documentId,
      filename,
      filePath
    });

    try {
      const fileUrl = config.API_URL.replace('/api/', '/') + filePath;

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = fileUrl;
      link.target = '_blank';
      link.download = filename;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('✅ Download started successfully!');
    } catch (error) {
      console.error('Error initiating download:', error);
      toast.error('❌ Failed to start download');
    }
  };

  const fetchDocumentVersions = async (documentType) => {
    if (!user?.id) return;

    try {
      const companyId = await getCompanyId();

      const response = await fetchUserDocuments(companyId, 'versions', 'all', documentType);

      if (response.success) {
        setDocumentVersions(response.data);
      } else {
        toast.error(`❌ Error: ${response.message}`);
      }
    } catch (error) {
      console.error('Error fetching document versions:', error);
      toast.error('❌ Failed to fetch document versions');
    }
  };

  const showVersions = (document) => {
    setSelectedDocument(document);
    setShowVersionModal(true);
    fetchDocumentVersions(document.document_type);
  };

  useEffect(() => {
    if (user?.id && profileComplete) {
      fetchDocuments();
    }
  }, [user?.id, profileComplete, groupBy, statusFilter]);

  useEffect(() => {
    return () => {
      if (viewingDocument?.cleanup) {
        viewingDocument.cleanup();
      }
    };
  }, [viewingDocument]);

  // Initialize Magnific Popup
  useEffect(() => {
    // This useEffect is no longer needed as Swal.fire handles the lightbox
    // $('.popup-link').magnificPopup({
    //   type: 'image',
    //   gallery: {
    //     enabled: true
    //   },
    //   callbacks: {
    //     elementParse: function(item) {
    //       // Function to determine content type
    //       if(item.el[0].getAttribute('data-type') === 'pdf') {
    //         item.type = 'iframe';
    //       } else {
    //         item.type = 'image';
    //       }
    //     }
    //   }
    // });
  }, [documents]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Update the loading and error states display
  if (!user?.id) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>Authentication Required</Alert.Heading>
          <p>Please log in to access the documents section.</p>
        </Alert>
      </Container>
    );
  }

  if (!profileComplete) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>Profile Completion Required</Alert.Heading>
          <p>
            Please complete your profile (currently at {profileCompletion}%) to access the documents section.
            This includes uploading all required company documents.
          </p>
          <hr />
          <p className="mb-0">
            <Button variant="primary" href="/members/profile">
              Complete Profile
            </Button>
          </p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading documents...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2 className="fw-bold text-primary">My Documents</h2>
          <p className="text-muted">Manage your uploaded documents and their versions</p>
        </Col>
        <Col xs="auto">
          <Button
            variant="primary"
            onClick={() => setShowUploadModal(true)}
            className="me-2"
          >
            <FaUpload className="me-2" />
            Upload Document
          </Button>
        </Col>
      </Row>

      {/* Enhanced Search and Filter Controls */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Search Documents</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search by filename, type, or company..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    disabled={searching}
                  />
                  {searching ? (
                    <Button variant="outline-primary" disabled>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    </Button>
                  ) : searchQuery ? (
                    <Button variant="outline-secondary" onClick={clearSearch}>
                      <FaTimes />
                    </Button>
                  ) : (
                    <Button variant="outline-primary" disabled>
                      <FaSearch />
                    </Button>
                  )}
                </InputGroup>
                <Form.Text className="text-muted">
                  Start typing to search documents
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Group By</Form.Label>
                <Form.Select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="mb-2"
                >
                  <option value="type">Document Type</option>
                  <option value="date">Upload Date</option>
                  <option value="status">Status</option>
                  <option value="none">No Grouping</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Status Filter</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="mb-2"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Document Type</Form.Label>
                <Form.Select
                  value={uploadData.document_type}
                  onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
                  className="mb-2"
                >
                  <option value="">All Types</option>
                  {documentTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Active Status</Form.Label>
                <Form.Select
                  className="mb-2"
                  onChange={(e) => {
                    const value = e.target.value;
                    setStatusFilter(value === 'all' ? 'all' : value === 'true' ? 'active' : 'inactive');
                  }}
                  value={statusFilter === 'all' ? 'all' : statusFilter === 'active' ? 'true' : 'false'}
                >
                  <option value="all">All Documents</option>
                  <option value="true">Active Only</option>
                  <option value="false">Inactive Only</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Active Filters Display */}
          <div className="mt-3">
            {(searchQuery || statusFilter !== 'all' || uploadData.document_type || groupBy !== 'type') && (
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted">Active Filters:</small>
                {searchQuery && (
                  <Badge bg="info" className="d-flex align-items-center">
                    Search: {searchQuery}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2 text-white"
                      onClick={clearSearch}
                    >
                      ×
                    </Button>
                  </Badge>
                )}
                {statusFilter !== 'all' && (
                  <Badge bg="info" className="d-flex align-items-center">
                    Status: {statusFilter}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2 text-white"
                      onClick={() => {
                        setStatusFilter('all');
                        fetchDocuments();
                      }}
                    >
                      ×
                    </Button>
                  </Badge>
                )}
                {uploadData.document_type && (
                  <Badge bg="info" className="d-flex align-items-center">
                    Type: {getDocumentTypeLabel(uploadData.document_type)}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2 text-white"
                      onClick={() => {
                        setUploadData({ ...uploadData, document_type: '' });
                        fetchDocuments();
                      }}
                    >
                      ×
                    </Button>
                  </Badge>
                )}
                {groupBy !== 'type' && (
                  <Badge bg="info" className="d-flex align-items-center">
                    Grouped by: {groupBy}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2 text-white"
                      onClick={() => {
                        setGroupBy('type');
                        fetchDocuments();
                      }}
                    >
                      ×
                    </Button>
                  </Badge>
                )}
                <Button
                  variant="link"
                  size="sm"
                  className="text-muted"
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setUploadData({ ...uploadData, document_type: '' });
                    setGroupBy('type');
                    fetchDocuments();
                  }}
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Documents Display - Use filteredDocuments instead of documents */}
      {filteredDocuments.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            {searching ? (
              <>
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h5>Searching documents...</h5>
              </>
            ) : searchQuery ? (
              <>
                <FaFile size={48} className="text-muted mb-3" />
                <h5>No documents found matching "{searchQuery}"</h5>
                <p className="text-muted">Try adjusting your search terms</p>
                <Button variant="primary" onClick={clearSearch}>
                  Clear Search
                </Button>
              </>
            ) : (
              <>
                <FaFile size={48} className="text-muted mb-3" />
                <h5>No documents found</h5>
                <p className="text-muted">Upload your first document to get started</p>
                <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                  <FaUpload className="me-2" />
                  Upload Document
                </Button>
              </>
            )}
          </Card.Body>
        </Card>
      ) : (
        <div>
          {filteredDocuments.map((docGroup) => (
            <div key={docGroup.document_type} className="mb-4">
              <Card>
                <Card.Header>
                  <h5 className="mb-0">{docGroup.document_name}</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ width: '15%' }}>File</th>
                        <th style={{ width: '20%' }}>Company Info</th>
                        <th style={{ width: '15%' }}>Upload Info</th>
                        <th style={{ width: '15%' }}>Status</th>
                        <th style={{ width: '20%' }}>Description</th>
                        <th style={{ width: '10%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {docGroup.documents.map((doc, index) => (
                        <tr key={doc.id} className={doc.is_active ? 'table-success' : ''}>
                          <td>{index + 1}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {getFileIcon(doc.document_name)}
                              <div className="ms-2">
                                <div className="text-truncate" style={{ maxWidth: '200px' }} title={doc.original_filename}>
                                  {doc.original_filename}
                                </div>
                                <small className="text-muted">{doc.file_info.size_formatted}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>{doc.company_info.company_name}</div>
                            <small className="text-muted">Reg: {doc.company_info.registration_number}</small>
                          </td>
                          <td>
                            <div>Version: {doc.version_number}</div>
                            <small className="text-muted">{formatDate(doc.created_at)}</small>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <Badge
                                bg={doc.status === 'approved' ? 'success' :
                                  doc.status === 'pending' ? 'warning' : 'secondary'}
                                className="me-2"
                              >
                                {doc.status}
                              </Badge>
                              {doc.is_active && (
                                <Badge bg="success" className="ms-1">
                                  <FaCheck className="me-1" />
                                  Active
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '250px' }} title={doc.description}>
                              {doc.description || '-'}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewDocument(doc)}
                                className="popup-link"
                                data-type={doc.document_name.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image'}
                                title="View Document"
                              >
                                <FaEye />
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleDownload(doc.id, doc.original_filename, doc.file_path)}
                                title="Download"
                              >
                                <FaDownload />
                              </Button>
                              {!doc.is_active && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleSetActive(doc.id, doc.document_type)}
                                  title="Set as Active"
                                >
                                  <FaCheck />
                                </Button>
                              )}
                              {/* Delete button - only show for inactive documents when company status is pending */}
                              {!doc.is_active && companyStatus === 'pending' && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteDocument(doc.id, doc.original_filename)}
                                  title="Delete Document"
                                  disabled={deleting}
                                >
                                  <FaTrash />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Upload Document</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpload}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Document Type *</Form.Label>
                  <Form.Select
                    value={uploadData.document_type}
                    onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
                    required
                  >
                    <option value="">Select document type...</option>
                    {documentTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>File *</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png,image/jpg"
                    onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                    required
                  />
                  <Form.Text className="text-muted">
                    Accepted formats: PDF, JPG, JPEG, PNG (Max size: 10MB)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={uploadData.notes}
                onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
                placeholder="Add any notes about this document..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <FaUpload className="me-2" />
                  Upload Document
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Versions Modal */}
      <Modal show={showVersionModal} onHide={() => setShowVersionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Document Versions - {selectedDocument && getDocumentTypeLabel(selectedDocument.document_type)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {documentVersions.length === 0 ? (
            <div className="text-center py-4">
              <p>No versions found for this document type.</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: '10%' }}>Version</th>
                  <th style={{ width: '25%' }}>File</th>
                  <th style={{ width: '25%' }}>Company</th>
                  <th style={{ width: '15%' }}>Status</th>
                  <th style={{ width: '15%' }}>Uploaded</th>
                  <th style={{ width: '10%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documentVersions.map((version) => (
                  <tr key={version.id} className={version.is_active ? 'table-success' : ''}>
                    <td>
                      <div className="d-flex align-items-center">
                        <strong>{version.version_number}</strong>
                        {version.is_active && (
                          <Badge bg="success" className="ms-2">
                            <FaCheck className="me-1" />
                            Active
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        {getFileIcon(version.document_name)}
                        <div className="ms-2">
                          <div className="text-truncate" style={{ maxWidth: '200px' }} title={version.original_filename}>
                            {version.original_filename}
                          </div>
                          <small className="text-muted">
                            {version.file_info?.size_formatted || formatFileSize(version.file_size)}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>{version.company_info?.company_name}</div>
                      <small className="text-muted">
                        Reg: {version.company_info?.registration_number}
                      </small>
                    </td>
                    <td>
                      <Badge
                        bg={version.status === 'approved' ? 'success' :
                          version.status === 'pending' ? 'warning' : 'secondary'}
                      >
                        {version.status}
                      </Badge>
                    </td>
                    <td>{formatDate(version.created_at)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleDownload(version.id, version.original_filename, version.file_path)}
                          title="Download"
                        >
                          <FaDownload />
                        </Button>
                        {!version.is_active && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleSetActive(version.id, version.document_type)}
                            title="Set as Active"
                          >
                            <FaCheck />
                          </Button>
                        )}
                        {/* Delete button - only show for inactive documents when company status is pending */}
                        {!version.is_active && companyStatus === 'pending' && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteDocument(version.id, version.original_filename)}
                            title="Delete Document"
                            disabled={deleting}
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVersionModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Document Viewer Modal */}
      <Modal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          if (viewingDocument?.cleanup) {
            viewingDocument.cleanup();
          }
          setViewingDocument(null);
          setDocumentUrl(null);
        }}
        size="lg"
        fullscreen
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {viewingDocument?.original_filename}
            <Button
              variant="link"
              className="ms-2"
              onClick={() => handleDownload(viewingDocument?.id, viewingDocument?.original_filename, viewingDocument?.file_path)}
              title="Download Document"
            >
              <FaDownload />
            </Button>
            <Button
              variant="link"
              className="ms-2"
              onClick={() => {
                if (documentUrl) {
                  window.open(documentUrl, '_blank');
                }
              }}
              title="Open in New Tab"
            >
              <FaExternalLinkAlt />
            </Button>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 bg-light">
          {viewerLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading document...</p>
            </div>
          ) : viewerError ? (
            <Alert variant="danger" className="m-3">
              {viewerError}
            </Alert>
          ) : documentUrl ? (
            <div className="document-viewer h-100 d-flex align-items-center justify-content-center">
              {viewingDocument?.document_name?.toLowerCase().endsWith('.pdf') ? (
                <object
                  data={documentUrl}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  className="border-0"
                >
                  <p>Your browser does not support PDFs.
                    <a href={documentUrl} target="_blank" rel="noopener noreferrer">Click here to view</a>
                  </p>
                </object>
              ) : (
                <img
                  src={documentUrl}
                  alt={viewingDocument?.original_filename}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              )}
            </div>
          ) : (
            <Alert variant="warning" className="m-3">
              No document to display
            </Alert>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Documents; 